import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  History,
  CalendarToday,
  AttachMoney,
  People,
  LocationOn,
  AccessTime,
  Cancel,
  CheckCircle,
  Warning,
  Info,
  Print,
  Download
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { bookingService } from '../services/firebase';
import { Booking } from '../types';
import { useTranslation } from 'react-i18next';

const BookingHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t, i18n } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return t('common.notSpecified');
    const locale = i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'en' ? 'en-US' : 'tr-TR';
    try {
      const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString(locale);
    } catch (e) {
      return t('common.invalidDate');
    }
  };

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    } else {
      fetchBookings();
    }
  }, [currentUser, navigate]);

  const fetchBookings = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const userBookings = await bookingService.getUserBookings(currentUser.uid);
      // Sadece kullanıcının kendi rezervasyonlarını göster (misafir rezervasyonları hariç)
      const userOwnBookings = userBookings.filter(booking => !booking.isGuestBooking);
      setBookings(userOwnBookings);
    } catch (err) {
      setError(t('bookingHistoryPage.errors.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCancelBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!selectedBooking) return;
    try {
      await bookingService.updateBookingStatus(selectedBooking.id, 'cancelled');
      fetchBookings();
    } catch (err) {
      setError(t('bookingHistoryPage.errors.cancelError'));
    } finally {
      setCancelDialogOpen(false);
      setSelectedBooking(null);
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    const colors = {
      confirmed: 'success',
      pending: 'warning',
      cancelled: 'error',
      completed: 'info',
    } as const;
    return colors[status as keyof typeof colors] || 'default';
  };

  const getStatusText = (status: string) => t(`bookingStatus.${status}`, status);

  const getStatusIcon = (status: string) => {
    const icons = {
      confirmed: <CheckCircle />,
      pending: <Warning />,
      cancelled: <Cancel />,
      completed: <Info />
    };
    return icons[status as keyof typeof icons] || <Info />;
  };

  const filteredBookings = bookings.filter(booking => {
    if (tabValue === 1) return ['confirmed', 'pending'].includes(booking.status);
    if (tabValue === 2) return booking.status === 'completed';
    if (tabValue === 3) return booking.status === 'cancelled';
    return true; // tabValue === 0
  });

  return (
    <>
      <Helmet>
        <title>{t('bookingHistoryPage.helmetTitle')} - HatTravel</title>
        <meta name="description" content={t('bookingHistoryPage.helmetDescription')} />
      </Helmet>
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <History sx={{ fontSize: 40, mr: 2, color: 'var(--navy-blue)' }} />
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('bookingHistoryPage.title')}
          </Typography>
        </Box>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="booking history tabs" variant="scrollable" scrollButtons="auto">
              <Tab label={t('bookingHistoryPage.tabs.all')} />
              <Tab label={t('bookingHistoryPage.tabs.active')} />
              <Tab label={t('bookingHistoryPage.tabs.past')} />
              <Tab label={t('bookingHistoryPage.tabs.cancelled')} />
            </Tabs>
          </Box>
          
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : filteredBookings.length === 0 ? (
              <Typography sx={{ textAlign: 'center', py: 6 }}>{t('bookingHistoryPage.noBookingsInTab')}</Typography>
            ) : (
              filteredBookings.map(booking => (
                <Card key={booking.id} sx={{ mb: 3 }} variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="h6" component="h2" gutterBottom>{booking.tourName}</Typography>
                        <Chip icon={getStatusIcon(booking.status)} label={getStatusText(booking.status)} color={getStatusColor(booking.status)} size="small" />
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="text.secondary">{t('bookingHistoryPage.bookingDate')}</Typography>
                        <Typography variant="subtitle2">{formatDate(booking.bookingDate)}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 3 }}>
                      <Box><Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}><LocationOn fontSize="inherit" sx={{ mr: 0.5 }} />{t('bookingHistoryPage.destination')}</Typography><Typography variant="body1">{booking.tourDetails?.destination}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}><AccessTime fontSize="inherit" sx={{ mr: 0.5 }} />{t('bookingHistoryPage.duration')}</Typography><Typography variant="body1">{booking.tourDetails?.duration}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}><People fontSize="inherit" sx={{ mr: 0.5 }} />{t('bookingHistoryPage.participants')}</Typography><Typography variant="body1">{booking.participants}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}><AttachMoney fontSize="inherit" sx={{ mr: 0.5 }} />{t('bookingHistoryPage.totalPrice')}</Typography><Typography variant="body1">{booking.totalPrice} {booking.currency}</Typography></Box>
                    </Box>
                  </CardContent>
                  <DialogActions>
                    <Tooltip title={t('bookingHistoryPage.actions.print')}><IconButton onClick={() => window.print()}><Print /></IconButton></Tooltip>
                    <Tooltip title={t('bookingHistoryPage.actions.download')}><IconButton><Download /></IconButton></Tooltip>
                    {['pending', 'confirmed'].includes(booking.status) && (
                      <Button color="error" startIcon={<Cancel />} onClick={() => handleCancelBooking(booking)}>{t('bookingHistoryPage.actions.cancel')}</Button>
                    )}
                  </DialogActions>
                </Card>
              ))
            )}
          </CardContent>
        </Card>
      </Container>
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>{t('bookingHistoryPage.cancelDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>{t('bookingHistoryPage.cancelDialog.content', { tourName: selectedBooking?.tourName })}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{t('bookingHistoryPage.cancelDialog.note')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>{t('bookingHistoryPage.cancelDialog.no')}</Button>
          <Button onClick={handleConfirmCancel} color="error" autoFocus>{t('bookingHistoryPage.cancelDialog.yes')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BookingHistoryPage; 